import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTagsList, createTag } from "../../../redux/Thunk/Product/ProductThunks";
import Plus from "../../common/icons/Plus";

const PRESET_COLORS = [
  "#8B5CF6", // Purple (NRX/H1)
  "#EF4444", // Red (Obsolete)
  "#DC2626", // Crimson (High Risk)
  "#F59E0B", // Amber (LASA)
  "#06B6D4", // Cyan (Cold Chain)
  "#3B82F6", // Blue (Schedule H)
  "#10B981", // Emerald (OTC)
  "#EC4899", // Pink (Fast Expiry)
  "#6366F1", // Indigo (Critical Supply)
  "#64748B", // Slate (Hazardous)
];

const ProductTagsSelector = ({
  selectedTags = [],
  onTagsChange,
  readOnly = false,
  label = "Product Tags & Checkpoints",
  labelClassName = "block text-primary-400 text-[13px] leading-[20px]",
  placeholder = "Click to select or create product tags...",
  inlineDropdown = false,
}) => {
  const dispatch = useDispatch();
  const { tagsList = [] } = useSelector((state) => state.product);

  const [isOpen, setIsOpen] = useState(false);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagDescription, setNewTagDescription] = useState("");
  const [newTagColor, setNewTagColor] = useState("#3B82F6");
  const [isCreating, setIsCreating] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    dispatch(fetchTagsList());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateInput(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Normalize selected tags into array of objects { id, name, color, category }
  const normalizedSelectedTags = selectedTags.map((t) => {
    if (typeof t === "object" && t !== null) {
      return t;
    }
    // If it's ID or Name
    const found = tagsList.find((item) => item.id === t || item.name === t);
    return (
      found || {
        id: t,
        name: String(t),
        color: "#3B82F6",
        category: "General",
      }
    );
  });

  const handleSelectTag = (tag) => {
    const exists = normalizedSelectedTags.some(
      (t) => t.id === tag.id || t.name?.toUpperCase() === tag.name?.toUpperCase()
    );
    let updated;
    if (exists) {
      updated = normalizedSelectedTags.filter(
        (t) => t.id !== tag.id && t.name?.toUpperCase() !== tag.name?.toUpperCase()
      );
    } else {
      updated = [...normalizedSelectedTags, tag];
    }
    if (onTagsChange) {
      onTagsChange(updated);
    }
  };

  const handleRemoveTag = (e, tag) => {
    e.stopPropagation();
    if (readOnly) return;
    const updated = normalizedSelectedTags.filter(
      (t) => t.id !== tag.id && t.name?.toUpperCase() !== tag.name?.toUpperCase()
    );
    if (onTagsChange) {
      onTagsChange(updated);
    }
  };

  const handleCreateNewTag = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) return;

    setIsCreating(true);
    try {
      const result = await dispatch(
        createTag({
          name: newTagName.trim(),
          description: newTagDescription.trim() || undefined,
          color: newTagColor,
        })
      ).unwrap();

      const createdTag = result?.tag;
      if (createdTag) {
        handleSelectTag(createdTag);
      }
      setNewTagName("");
      setNewTagDescription("");
      setShowCreateInput(false);
    } catch (err) {
      console.error("Error creating tag:", err);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="w-full relative" ref={dropdownRef}>
      {label && (
        <label className={labelClassName}>
          {label}
        </label>
      )}

      {/* Selected Tags Display */}
      <div
        className={`min-h-[42px] p-2 bg-white border border-primary-200 rounded-[8px] flex flex-wrap items-center gap-1.5 ${
          !readOnly ? "cursor-pointer hover:border-primary-400" : ""
        }`}
        onClick={() => !readOnly && setIsOpen(!isOpen)}
      >
        {normalizedSelectedTags.length > 0 ? (
          normalizedSelectedTags.map((tag) => (
            <span
              key={tag.id || tag.name}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium text-white shadow-sm transition-all"
              style={{ backgroundColor: tag.color || "#3B82F6" }}
            >
              <span>{tag.name}</span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => handleRemoveTag(e, tag)}
                  className="hover:opacity-75 focus:outline-none ml-0.5"
                >
                  ✕
                </button>
              )}
            </span>
          ))
        ) : (
          <span className="text-[13px] text-gray-400 font-normal italic">
            {readOnly ? "No tags assigned" : placeholder}
          </span>
        )}
      </div>

      {/* Dropdown Options */}
      {isOpen && !readOnly && (
        <div
          className={
            inlineDropdown
              ? "mt-2 bg-white border border-primary-200 rounded-xl shadow-xs p-3 max-h-[260px] overflow-y-auto"
              : "absolute left-0 right-0 top-full mt-1 bg-white border border-primary-200 rounded-[8px] shadow-xl z-50 p-3 max-h-[300px] overflow-y-auto"
          }
        >
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
            <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider">
              Universal Preset Tags
            </span>
            <button
              type="button"
              onClick={() => setShowCreateInput(!showCreateInput)}
              className="text-[12px] font-medium text-primary-600 hover:text-primary-800 flex items-center gap-1"
            >
              <Plus width={14} height={14} />
              {showCreateInput ? "Cancel" : "Create New Tag"}
            </button>
          </div>

          {/* Inline Create Form */}
          {showCreateInput && (
            <form onSubmit={handleCreateNewTag} className="mb-3 p-2.5 bg-primary-50 rounded-[6px] border border-primary-100">
              <span className="block text-[12px] font-semibold text-primary-700 mb-1.5">
                New Universal Tag
              </span>
              <input
                type="text"
                placeholder="Tag Name (e.g., NRX/H1, Cold Chain)"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                className="w-full text-[12px] p-1.5 border border-primary-200 rounded mb-2 bg-white focus:outline-none focus:border-primary-500"
                autoFocus
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={newTagDescription}
                onChange={(e) => setNewTagDescription(e.target.value)}
                className="w-full text-[12px] p-1.5 border border-primary-200 rounded mb-2 bg-white focus:outline-none focus:border-primary-500"
              />
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] text-gray-600">Color:</span>
                <div className="flex items-center gap-1">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewTagColor(c)}
                      className={`w-4 h-4 rounded-full border ${
                        newTagColor === c ? "ring-2 ring-offset-1 ring-primary-500" : ""
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
              <button
                type="submit"
                disabled={isCreating || !newTagName.trim()}
                className="w-full text-[12px] py-1 bg-primary-600 text-white rounded font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {isCreating ? "Saving..." : "Add & Assign Tag"}
              </button>
            </form>
          )}

          {/* Tags List */}
          <div className="flex flex-wrap gap-1.5">
            {tagsList.length > 0 ? (
              tagsList.map((tag) => {
                const isSelected = normalizedSelectedTags.some(
                  (t) => t.id === tag.id || t.name?.toUpperCase() === tag.name?.toUpperCase()
                );
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleSelectTag(tag)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium border transition-all ${
                      isSelected
                        ? "text-white shadow-sm ring-2 ring-offset-1 ring-primary-400"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                    }`}
                    style={isSelected ? { backgroundColor: tag.color || "#3B82F6", borderColor: tag.color } : {}}
                  >
                    <span>{isSelected ? "✓" : "+"}</span>
                    <span>{tag.name}</span>
                  </button>
                );
              })
            ) : (
              <span className="text-[12px] text-gray-500 py-1">
                No preset tags loaded. Click "Create New Tag" to add one!
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTagsSelector;
